import { useCallback, useEffect, useRef, useState } from "react";
import {
  AppBar,
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Collapse,
  Container,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Popover,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  ListItemButton,
} from "@mui/material";
import HomeRounded from "@mui/icons-material/HomeRounded";
import TrackChangesOutlined from "@mui/icons-material/TrackChangesOutlined";
import ApartmentOutlined from "@mui/icons-material/ApartmentOutlined";
import HeadsetMicOutlined from "@mui/icons-material/HeadsetMicOutlined";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import PeopleAltRounded from "@mui/icons-material/PeopleAltRounded";
import LockResetRounded from "@mui/icons-material/LockResetRounded";
import BusinessRounded from "@mui/icons-material/BusinessRounded";
import LogoutRounded from "@mui/icons-material/LogoutRounded";
import SettingsRounded from "@mui/icons-material/SettingsRounded";
import MenuRounded from "@mui/icons-material/MenuRounded";
import NotificationsRounded from "@mui/icons-material/NotificationsRounded";
import DarkModeRounded from "@mui/icons-material/DarkModeRounded";
import LightModeRounded from "@mui/icons-material/LightModeRounded";
import VolumeOffRounded from "@mui/icons-material/VolumeOffRounded";
import VolumeUpRounded from "@mui/icons-material/VolumeUpRounded";
import ChevronLeftRounded from "@mui/icons-material/ChevronLeftRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import LockOutlined from "@mui/icons-material/LockOutlined";
import ChecklistOutlined from "@mui/icons-material/ChecklistOutlined";
import AccountTreeOutlined from "@mui/icons-material/AccountTreeOutlined";
import DashboardCustomizeOutlined from "@mui/icons-material/DashboardCustomizeOutlined";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import BarChartRounded from "@mui/icons-material/BarChartRounded";
import AccountBalanceWalletOutlined from "@mui/icons-material/AccountBalanceWalletOutlined";
import RequestQuoteOutlined from "@mui/icons-material/RequestQuoteOutlined";
import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import axios from "axios";
import AdminCompaniesModal from "./Gestao/AdminCompaniesModal";
import useContratosAtivos from "../hooks/useContratosAtivos";
import { canAccessAipimIncorp } from "../utils/aipimIncorpAccess";
import { canUseNovaEstruturaPortal } from "../utils/novaEstruturaPortal";
import { clearContabilidadeApiCache } from "../features/agenteContabilidade/services/agenteContabilidadeApiService";
import notificacoesService from "../features/notificacoes/services/notificacoes.service";
import { canAccessAipoPreview } from "../features/aipo/access";
import { canAccessGestaoCustos } from "../features/gestaoCustos/access";
import { canAccessGestaoEquipamentos } from "../features/gestaoEquipamentos/access";
import { canAccessSuporte } from "../features/suporte/access";
import { PORTAL_THEME_MODE_STORAGE_KEY } from "./theme/theme";
import FrontLoader from "@mui/icons-material/FrontLoader";

const DESKTOP_SIDEBAR_WIDTH = 264;
const SIDEBAR_STORAGE_KEY = "portal_sidebar_hidden";
const CONTEXTUAL_HEADER_AREAS = [
  { path: "/gestao-custos", label: "Gestão de Custos" },
  { path: "/gestao-equipamentos", label: "Equipamentos" },
  { path: "/cobranca", label: "Inadimplência" },
];
const SIDEBAR_OPEN_EASING = "cubic-bezier(0.2, 0.8, 0.2, 1)";
const NOTIFICATIONS_POLLING_MS = 60000;
const NOTIFICATION_SOUND_STORAGE_KEY = "portal_notification_sound_enabled";

const getInitialNotificationSoundEnabled = () => {
  try {
    return localStorage.getItem(NOTIFICATION_SOUND_STORAGE_KEY) !== "0";
  } catch (error) {
    return true;
  }
};

const formatNotificationDate = (value) => {
  if (!value) return "";

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return "";

  return new Intl.DateTimeFormat("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
};

const getNotificationId = (notification) =>
  notification?.id ??
  notification?.idNotificacao ??
  notification?.id_notificacao ??
  notification?.IdNotificacao ??
  notification?.Id_Notificacao ??
  null;

const isNotificationRead = (notification) =>
  notification?.lida === true ||
  notification?.lida === 1 ||
  notification?.lida === "true" ||
  notification?.lida === "1";

const getNotificationType = (notification) =>
  typeof notification?.tipo === "string" ? notification.tipo.trim().toLowerCase() : "";

const getUserFromToken = () => {
  try {
    const token =
      localStorage.getItem("jwtToken") ||
      localStorage.getItem("authToken") ||
      sessionStorage.getItem("token");

    if (!token) return { token: null, nome: "Usuario", email: "", role: null };

    const cleanToken = token.startsWith("Bearer ") ? token.slice(7) : token;
    const decoded = jwtDecode(cleanToken);

    return {
      token: cleanToken,
      nome: decoded?.nome || decoded?.name || "Usuario",
      email: decoded?.email || decoded?.sub || "",
      role: decoded?.role || null,
      idCliente:
        decoded?.id_cliente ??
        decoded?.idCliente ??
        decoded?.idClienteAipim ??
        decoded?.cliente_id ??
        null,
      nomeCliente:
        decoded?.nomeCliente ||
        decoded?.nome_cliente ||
        decoded?.cliente ||
        decoded?.client_name ||
        "",
    };
  } catch (error) {
    return { token: null, nome: "Usuario", email: "", role: null, nomeCliente: "" };
  }
};

const getSelectedCompanyName = () => {
  try {
    const selectedCompany = JSON.parse(localStorage.getItem("selectedCompany") || "null");
    return selectedCompany?.name || selectedCompany?.nome || selectedCompany?.nomeCliente || "";
  } catch (error) {
    return "";
  }
};

const getContratoProdutoId = (contrato) =>
  Number(
    contrato?.idProduto ??
      contrato?.id_produto ??
      contrato?.idProdutoAipim ??
      contrato?.id_produto_aipim ??
      contrato?.produto_id ??
      contrato?.id
  );

const createNavSections = ({ role, contratos, showAipimIncorp, showAipo, showGestaoCustos, showGestaoEquipamentos, showSuporte, usaNovaEstruturaPortal }) => {
  const suporteSections = showSuporte
    ? [{ label: "Suporte", to: "/suporte", icon: HeadsetMicOutlined, variant: "sectionLink" }]
    : [];

  if (role === "corretor") {
    return [
      {
        label: "Área do Corretor",
        to: "/area-corretor",
        icon: ApartmentOutlined,
      },
      ...(showAipo ? [{ label: "{AIPO}", to: "/aipo" }] : []),
      {
        label: "Conta",
        items: [
          {
            label: "Alterar senha",
            to: "/EditarSenha",
            icon: LockResetRounded,
            enabled: true,
          },
        ],
      },
      ...suporteSections,
    ];
  }

  const hasProduct = (productId) =>
    contratos.some((item) => getContratoProdutoId(item) === productId && item?.ativo !== false);

  return [
    {
      label: "Home",
      to: "/home_logado",
      icon: HomeRounded,
    },
    {
      label: usaNovaEstruturaPortal ? "Opera\u00e7\u00f5es" : "Agentes",
      items: [
        {
          label: usaNovaEstruturaPortal ? "Pr\u00e9 Venda" : "Agente de Pr\u00e9 Venda",
          to: "/sdr",
          icon: TrackChangesOutlined,
          enabled: hasProduct(2),
        },
        {
          label: usaNovaEstruturaPortal ? "Corretores" : "Agente do Corretor",
          to: "/assistente-vendas",
          icon: ApartmentOutlined,
          enabled: hasProduct(1),
        },
        {
          label: usaNovaEstruturaPortal ? "P\u00f3s-Venda" : "Agente de Pos-Venda",
          to: "/assistente-pos-venda",
          icon: HeadsetMicOutlined,
          enabled: hasProduct(5),
        },
        ...(usaNovaEstruturaPortal
          ? [
              {
                label: "Inadimpl\u00eancia",
                to: "/cobranca",
                icon: AccountBalanceWalletOutlined,
                enabled: hasProduct(6),
              },
            ]
          : []),
        {
          label: usaNovaEstruturaPortal ? "Conte\u00fado" : "Agente de Conteudo",
          to: "/assistente-conteudo",
          icon: DescriptionOutlined,
          enabled: hasProduct(3),
        },
        {
          label: usaNovaEstruturaPortal ? "Contabilidade" : "Agente de Contabilidade",
          to: "/agentecontabilidade",
          icon: BarChartRounded,
          enabled: hasProduct(9),
        },
        ...(usaNovaEstruturaPortal && showGestaoCustos
          ? [{
              label: "Gestão de Custos",
              to: "/gestao-custos",
              icon: RequestQuoteOutlined,
              enabled: true,
            }]
          : []),
        ...(usaNovaEstruturaPortal && showGestaoEquipamentos
          ? [{ label: "Equipamentos", to: "/gestao-equipamentos", icon: FrontLoader, enabled: true }]
          : []),
      ],
    },
    ...(showAipo
      ? [{ label: "{AIPO}", to: "/aipo", variant: "sectionLink" }]
      : []),
    ...(usaNovaEstruturaPortal
      ? [
          {
            label: "Agentes",
            to: "/agentes",
            variant: "sectionLink",
          },
        ]
      : []),
    ...(hasProduct(7)
      ? [
          {
            label: "TaskAgent",
            items: [
              {
                label: "Visao Geral",
                to: "/task-agent/inicio",
                icon: ChecklistOutlined,
                enabled: true,
              },
              {
                label: "Processos",
                to: "/task-agent/pastas",
                icon: DescriptionOutlined,
                enabled: true,
              },
            ],
          },
        ]
      : []),
    ...(usaNovaEstruturaPortal
      ? [
          {
            label: "Integra\u00e7\u00f5es",
            to: "/integracoes",
            variant: "sectionLink",
          },
          {
            label: "Governan\u00e7a",
            items: [
              {
                label: "Usu\u00e1rios",
                to: "/EditarUsuario",
                icon: PeopleAltRounded,
                enabled: true,
              },
              {
                label: "Auditoria",
                to: "/governanca/auditoria",
                icon: ChecklistOutlined,
                enabled: true,
              },
            ],
          },
        ]
      : []),
    ...(showAipimIncorp
      ? [
          {
            label: "Aipim Incorp",
            items: [
              {
                label: "Visao Geral",
                to: "/aipim-incorp/visao-geral",
                icon: InfoOutlined,
                enabled: true,
              },
              {
                label: "Inicio",
                to: "/aipim-incorp/inicio",
                icon: DashboardCustomizeOutlined,
                enabled: true,
              },
              {
                label: "Empreendimentos",
                to: "/aipim-incorp/empreendimentos",
                icon: BusinessRounded,
                enabled: true,
              },
              {
                label: "Processos",
                to: "/aipim-incorp/processos",
                icon: AccountTreeOutlined,
                enabled: true,
              },
            ],
          },
        ]
      : []),
    {
      label: "Conta",
      items: [
        ...(usaNovaEstruturaPortal
          ? [
              {
                label: "Contratos",
                to: "/contratos",
                icon: DescriptionOutlined,
                enabled: true,
              },
            ]
          : []),
        ...(!usaNovaEstruturaPortal
          ? [
              {
                label: "Usuarios",
                to: "/EditarUsuario",
                icon: PeopleAltRounded,
                enabled: true,
              },
            ]
          : []),
        {
          label: "Meu Cadastro",
          to: "/EditarSenha",
          icon: LockResetRounded,
          enabled: true,
        },
      ],
    },
    ...suporteSections,
  ];
};

const getDefaultExpandedSections = (sections) =>
  sections.reduce((expanded, section) => {
    if (section.items?.length) {
      expanded[section.label] = false;
    }
    return expanded;
  }, {});

const getInitialSidebarHidden = (role) => {
  if (role === "corretor") return true;

  try {
    return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "1";
  } catch (error) {
    return false;
  }
};

const getInitialExpandedSections = (sections) => getDefaultExpandedSections(sections);

const AppShell = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const isAgenteContabilidadeRoute = location.pathname.startsWith("/agentecontabilidade");
  const isDarkMode = theme.palette.mode === "dark";
  const notificationAudioContextRef = useRef(null);
  const previousNotificationsUnreadRef = useRef(null);
  const isCvcrmBrand = (() => {
    try {
      return localStorage.getItem("brand") === "cvcrm";
    } catch (error) {
      return false;
    }
  })();

  const user = getUserFromToken();
  const isCorretor = user.role === "corretor";
  const homePath = isCorretor ? "/area-corretor" : "/home_logado";
  const { contratosAtivos, clientName: contractsClientName } = useContratosAtivos();
  const selectedCompanyName = getSelectedCompanyName();
  const clientName = selectedCompanyName || contractsClientName || user.nomeCliente || "Portal do Cliente";
  const usaNovaEstruturaPortal = canUseNovaEstruturaPortal();
  const navSections = createNavSections({
    role: user.role,
    contratos: contratosAtivos,
    showAipimIncorp: canAccessAipimIncorp(),
    showAipo: canAccessAipoPreview(),
    showGestaoCustos: canAccessGestaoCustos(),
    showGestaoEquipamentos: canAccessGestaoEquipamentos(),
    showSuporte: canAccessSuporte(),
    usaNovaEstruturaPortal,
  });
  const showCompanies = user.role === "setup" || user.role === "aipim";

  const [sidebarHidden, setSidebarHidden] = useState(() => getInitialSidebarHidden(user.role));
  const [mobileDrawerOpen, setMobileDrawerOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState(() => getInitialExpandedSections(navSections));
  const [userMenuAnchor, setUserMenuAnchor] = useState(null);
  const [openCompaniesModal, setOpenCompaniesModal] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [isLoadingCompanies, setIsLoadingCompanies] = useState(false);
  const [notificationsAnchor, setNotificationsAnchor] = useState(null);
  const [notificationsUnread, setNotificationsUnread] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationsError, setNotificationsError] = useState("");
  const [notificationSoundEnabled, setNotificationSoundEnabled] = useState(getInitialNotificationSoundEnabled);
  const [taskAgentCurrentFolderId, setTaskAgentCurrentFolderId] = useState(null);
  const [taskAgentFolderPathIds, setTaskAgentFolderPathIds] = useState([]);
  const [taskAgentFolderTree, setTaskAgentFolderTree] = useState([]);
  const [taskAgentFolderExpanded, setTaskAgentFolderExpanded] = useState({});

  useEffect(() => {
    if (location.pathname.startsWith("/gestao-custos") || location.pathname.startsWith("/gestao-equipamentos") || location.pathname.startsWith("/cobranca")) {
      setExpandedSections((current) => ({ ...current, Operações: true }));
    }
  }, [location.pathname]);

  useEffect(() => {
    if (isCorretor) {
      setSidebarHidden(true);
    }
  }, [isCorretor]);

  const getNotificationAudioContext = useCallback(() => {
    if (typeof window === "undefined") return null;
    const AudioContextConstructor = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextConstructor) return null;

    if (!notificationAudioContextRef.current) {
      notificationAudioContextRef.current = new AudioContextConstructor();
    }

    return notificationAudioContextRef.current;
  }, []);

  const unlockNotificationAudio = useCallback(async () => {
    if (!notificationSoundEnabled) return;

    try {
      const audioContext = getNotificationAudioContext();
      if (audioContext?.state === "suspended") {
        await audioContext.resume();
      }
    } catch (error) {
      // Browsers may keep audio blocked until a user gesture.
    }
  }, [getNotificationAudioContext, notificationSoundEnabled]);

  const playNotificationSound = useCallback(async () => {
    if (!notificationSoundEnabled) return;

    try {
      const audioContext = getNotificationAudioContext();
      if (!audioContext) return;

      if (audioContext.state === "suspended") {
        await audioContext.resume();
      }

      const now = audioContext.currentTime;
      const masterGain = audioContext.createGain();
      masterGain.gain.setValueAtTime(0.0001, now);
      masterGain.gain.exponentialRampToValueAtTime(0.16, now + 0.015);
      masterGain.gain.exponentialRampToValueAtTime(0.0001, now + 0.38);
      masterGain.connect(audioContext.destination);

      [740, 988].forEach((frequency, index) => {
        const oscillator = audioContext.createOscillator();
        oscillator.type = "sine";
        oscillator.frequency.setValueAtTime(frequency, now + index * 0.11);
        oscillator.connect(masterGain);
        oscillator.start(now + index * 0.11);
        oscillator.stop(now + 0.34 + index * 0.04);
      });
    } catch (error) {
      // Silent fallback when the browser refuses programmatic audio.
    }
  }, [getNotificationAudioContext, notificationSoundEnabled]);

  useEffect(() => {
    try {
      localStorage.setItem(NOTIFICATION_SOUND_STORAGE_KEY, notificationSoundEnabled ? "1" : "0");
    } catch (error) {
      // ignore persistence errors
    }
  }, [notificationSoundEnabled]);

  useEffect(() => {
    const handleUserGesture = () => {
      unlockNotificationAudio();
    };

    window.addEventListener("pointerdown", handleUserGesture, { passive: true });
    window.addEventListener("keydown", handleUserGesture);

    return () => {
      window.removeEventListener("pointerdown", handleUserGesture);
      window.removeEventListener("keydown", handleUserGesture);
    };
  }, [unlockNotificationAudio]);

  const loadNotificationsSummary = useCallback(async () => {
    if (!user.token) return;

    try {
      const data = await notificacoesService.getResumo();
      const nextUnread = data.naoLidas || 0;
      const previousUnread = previousNotificationsUnreadRef.current;

      if (previousUnread !== null && nextUnread > previousUnread) {
        playNotificationSound();
      }

      previousNotificationsUnreadRef.current = nextUnread;
      setNotificationsUnread(nextUnread);
    } catch (error) {
      setNotificationsUnread(0);
    }
  }, [playNotificationSound, user.token]);

  const loadNotifications = useCallback(async () => {
    if (!user.token) return;

    try {
      setLoadingNotifications(true);
      setNotificationsError("");
      const data = await notificacoesService.listar({ limit: 20, offset: 0 });
      setNotifications(data.itens);
      await loadNotificationsSummary();
    } catch (error) {
      setNotificationsError(
          notificacoesService.getApiErrorMessage(error, "Erro ao carregar notificações.")
      );
      setNotifications([]);
    } finally {
      setLoadingNotifications(false);
    }
  }, [loadNotificationsSummary, user.token]);

  useEffect(() => {
    loadNotificationsSummary();

    const intervalId = window.setInterval(loadNotificationsSummary, NOTIFICATIONS_POLLING_MS);
    const handleFocus = () => loadNotificationsSummary();

    window.addEventListener("focus", handleFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", handleFocus);
    };
  }, [loadNotificationsSummary]);

  useEffect(() => {
    if (!isCorretor) return;

    const allowedCorretorRoutes = ["/area-corretor", "/EditarSenha", "/configuracoes", "/suporte"];
    const isAllowedRoute = allowedCorretorRoutes.some(
      (route) => location.pathname === route || location.pathname.startsWith(`${route}/`)
    );

    if (!isAllowedRoute) {
      navigate("/area-corretor", { replace: true });
    }
  }, [isCorretor, location.pathname, navigate]);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, sidebarHidden ? "1" : "0");
    } catch (error) {
      // ignore persistence errors
    }
  }, [sidebarHidden]);

  useEffect(() => {
    if (isDesktop) {
      setMobileDrawerOpen(false);
    }
  }, [isDesktop, location.pathname]);

  useEffect(() => {
    const handleTaskAgentFolderContext = (event) => {
      setTaskAgentCurrentFolderId(event.detail?.currentFolderId ?? null);
      setTaskAgentFolderPathIds(Array.isArray(event.detail?.folderPathIds) ? event.detail.folderPathIds : []);
      setTaskAgentFolderTree(Array.isArray(event.detail?.folderTree) ? event.detail.folderTree : []);
    };

    window.addEventListener("taskagent:folder-context", handleTaskAgentFolderContext);

    return () => window.removeEventListener("taskagent:folder-context", handleTaskAgentFolderContext);
  }, []);

  useEffect(() => {
    if (location.pathname !== "/task-agent/pastas") {
      setTaskAgentCurrentFolderId(null);
      setTaskAgentFolderPathIds([]);
      setTaskAgentFolderTree([]);
      setTaskAgentFolderExpanded({});
    }
  }, [location.pathname]);

  useEffect(() => {
    if (location.pathname !== "/task-agent/pastas") return;

    setTaskAgentFolderExpanded((current) => {
      const next = { ...current, __processos__: true };
      taskAgentFolderPathIds.forEach((folderId) => {
        next[String(folderId)] = true;
      });
      return next;
    });
  }, [location.pathname, taskAgentFolderPathIds]);

  const userInitial = (user.email || user.nome || "U").charAt(0).toUpperCase();
  const userLabel = user.email || user.nome || "Usuario";
  const roleLabel = clientName || user.role || "Portal do Cliente";
  const headerArea = CONTEXTUAL_HEADER_AREAS.find(
    ({ path }) => location.pathname === path || location.pathname.startsWith(`${path}/`)
  );
  const isSidebarCollapsed = isDesktop ? sidebarHidden : !mobileDrawerOpen;
  const showHeaderArea = isSidebarCollapsed && Boolean(headerArea);
  const headerTitle = showHeaderArea ? `${clientName} - ${headerArea.label}` : clientName;
  const isCvcrmDark = isCvcrmBrand && isDarkMode;
  const sidebarBackground = isCvcrmDark
    ? "linear-gradient(180deg, #0d221d 0%, #123029 46%, #102823 100%)"
    : isCvcrmBrand
      ? "linear-gradient(180deg, #12352d 0%, #18463b 48%, #0f2e27 100%)"
      : "linear-gradient(180deg, #0f153b 0%, #13153b 45%, #1b0f3f 100%)";

  const handleToggleSidebar = () => {
    if (isDesktop) {
      setSidebarHidden((current) => !current);
      return;
    }
    setMobileDrawerOpen((current) => !current);
  };

  const handleHideSidebar = () => {
    if (isDesktop) {
      setSidebarHidden(true);
    }
  };

  const handleToggleSection = (sectionLabel) => {
    setExpandedSections((current) => ({
      ...current,
      [sectionLabel]: !current[sectionLabel],
    }));
  };

  const handleNavItemClick = (to, enabled) => {
    if (!enabled) return;

    if (to === "/task-agent/pastas" && location.pathname === "/task-agent/pastas") {
      window.dispatchEvent(new CustomEvent("taskagent:open-root-pastas"));
    }

    setMobileDrawerOpen(false);
  };

  const handleTaskAgentFolderNavigation = (folderId) => {
    if (location.pathname === "/task-agent/pastas") {
      window.dispatchEvent(new CustomEvent("taskagent:navigate-folder", { detail: { folderId } }));
    }

    setMobileDrawerOpen(false);
  };

  const handleToggleTaskAgentFolder = (key) => {
    setTaskAgentFolderExpanded((current) => ({
      ...current,
      [key]: !current[key],
    }));
  };

  const openUserMenu = (event) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const closeUserMenu = () => {
    setUserMenuAnchor(null);
  };

  const openNotifications = (event) => {
    setNotificationsAnchor(event.currentTarget);
    loadNotifications();
  };

  const closeNotifications = () => {
    setNotificationsAnchor(null);
  };

  const handleToggleTheme = () => {
    const nextMode = isDarkMode ? "light" : "dark";
    localStorage.setItem(PORTAL_THEME_MODE_STORAGE_KEY, nextMode);
    window.location.reload();
  };

  const handleNotificationClick = async (notification) => {
    const notificationId = getNotificationId(notification);
    if (!notificationId) {
      setNotificationsError("Notificação sem identificador para marcar como lida.");
      return;
    }

    const wasUnread = !isNotificationRead(notification);

    if (wasUnread) {
      try {
        await notificacoesService.marcarComoLida(notificationId);
        setNotifications((current) =>
          current.map((item) => (getNotificationId(item) === notificationId ? { ...item, lida: true } : item))
        );
        setNotificationsUnread((current) => Math.max(0, current - 1));
      } catch (error) {
        setNotificationsError(
          notificacoesService.getApiErrorMessage(error, "Erro ao marcar notificação como lida.")
        );
        return;
      }
    }

    if (getNotificationType(notification) === "lead") {
      closeNotifications();
      setMobileDrawerOpen(false);
      navigate(`/area-corretor?chat=livia&notificacao=${encodeURIComponent(notificationId)}`);
    }
  };

  const handleMarkAllNotificationsRead = async () => {
    try {
      await notificacoesService.marcarTodasComoLidas();
      setNotifications((current) => current.map((item) => ({ ...item, lida: true })));
      setNotificationsUnread(0);
    } catch (error) {
      setNotificationsError(
        notificacoesService.getApiErrorMessage(error, "Erro ao marcar notificações como lidas.")
      );
    }
  };

  const handleToggleNotificationSound = () => {
    setNotificationSoundEnabled((current) => {
      const next = !current;

      if (next) {
        window.setTimeout(async () => {
          try {
            const audioContext = getNotificationAudioContext();
            if (audioContext?.state === "suspended") {
              await audioContext.resume();
            }
          } catch (error) {
            // Browser may still require another user gesture.
          }
        }, 0);
      }

      return next;
    });
  };

  const handleUserNavigation = (to) => {
    closeUserMenu();
    setMobileDrawerOpen(false);
    navigate(to);
  };

  const handleLogout = () => {
    closeUserMenu();
    clearContabilidadeApiCache();

    const brand = localStorage.getItem("brand");
    const leadsViewMode = localStorage.getItem("sdr_leads_view_mode");

    localStorage.clear();
    sessionStorage.clear();

    if (brand) {
      localStorage.setItem("brand", brand);
    }

    if (leadsViewMode) {
      localStorage.setItem("sdr_leads_view_mode", leadsViewMode);
    }

    navigate(brand ? "/cvcrm" : "/");
  };

  const handleOpenCompaniesModal = async () => {
    try {
      setIsLoadingCompanies(true);

      if (!user.token) {
        alert("Sessao expirada. Faca login novamente.");
        return;
      }

      const apiUrl = process.env.REACT_APP_API_BASE_URL;
      const companiesResponse = await axios.get(`${apiUrl}/empresas`, {
        headers: { Authorization: `Bearer ${user.token}` },
      });

      const mappedCompanies = (companiesResponse.data || []).map((company) => ({
        id: company.idCliente,
        name: company.nomeCliente,
        dataHoraRegistro: company.dataHoraRegistro,
      }));

      setCompanies(mappedCompanies);
      setOpenCompaniesModal(true);
      setMobileDrawerOpen(false);
      closeUserMenu();
    } catch (error) {
      console.error("Erro ao buscar empresas:", error);
      alert("Erro ao carregar lista de empresas.");
    } finally {
      setIsLoadingCompanies(false);
    }
  };

  const handleSelectCompany = async (company) => {
    try {
      const apiUrl = process.env.REACT_APP_API_BASE_URL;
      const response = await axios.post(
        `${apiUrl}/login/integrador`,
        { idCliente: company.id },
        { headers: { Authorization: `Bearer ${user.token}` } }
      );

      localStorage.setItem("jwtToken", response.data.token);
      localStorage.setItem("selectedCompany", JSON.stringify(company));

      setOpenCompaniesModal(false);
      navigate(homePath);
      window.dispatchEvent(new Event("empresaMudou"));
    } catch (error) {
      console.error("Erro ao selecionar empresa:", error);
      alert("Erro ao trocar de empresa. Tente novamente.");
    }
  };

  const navItemSx = {
    borderRadius: 2,
    color: "inherit",
    position: "relative",
    overflow: "hidden",
    transition: `transform 180ms ${SIDEBAR_OPEN_EASING}, background-color 200ms ${SIDEBAR_OPEN_EASING}, box-shadow 220ms ${SIDEBAR_OPEN_EASING}`,
    "& .MuiListItemIcon-root": {
      color: "rgba(245,248,255,0.9)",
      minWidth: 36,
      transition: `transform 180ms ${SIDEBAR_OPEN_EASING}, color 200ms ${SIDEBAR_OPEN_EASING}`,
    },
    "&:hover": {
      transform: "translateX(2px)",
      backgroundColor: isCvcrmDark
        ? "rgba(70, 164, 136, 0.12)"
        : isCvcrmBrand
          ? "rgba(75, 166, 134, 0.18)"
          : "rgba(70, 88, 214, 0.18)",
      boxShadow: isCvcrmDark
        ? "0 8px 22px rgba(4, 16, 14, 0.28), inset 0 0 0 1px rgba(126, 214, 188, 0.1)"
        : isCvcrmBrand
        ? "0 8px 22px rgba(10, 46, 38, 0.34), inset 0 0 0 1px rgba(130, 228, 196, 0.18)"
        : "0 8px 22px rgba(11, 16, 43, 0.32), inset 0 0 0 1px rgba(148, 160, 255, 0.18)",
    },
    "&:hover .MuiListItemIcon-root": {
      transform: "scale(1.06)",
    },
    "&.active": {
      background: isCvcrmDark
        ? "linear-gradient(92deg, rgba(32,88,74,0.78) 0%, rgba(48,116,98,0.68) 100%)"
        : isCvcrmBrand
        ? "linear-gradient(92deg, rgba(45,118,100,0.52) 0%, rgba(65,178,142,0.42) 100%)"
        : "linear-gradient(92deg, rgba(75,88,222,0.34) 0%, rgba(91,9,172,0.4) 100%)",
      boxShadow: isCvcrmDark
        ? "0 10px 24px rgba(4, 18, 15, 0.32), 0 0 0 1px rgba(120, 206, 182, 0.14), inset 0 0 18px rgba(88, 180, 154, 0.12)"
        : isCvcrmBrand
        ? "0 10px 24px rgba(12, 54, 44, 0.42), 0 0 0 1px rgba(122, 229, 192, 0.3), inset 0 0 26px rgba(84, 201, 162, 0.18)"
        : "0 10px 24px rgba(35, 23, 67, 0.42), 0 0 0 1px rgba(172, 136, 255, 0.36), inset 0 0 26px rgba(151, 101, 255, 0.21)",
    },
    "&.active .MuiListItemIcon-root": {
      color: "#fff",
      transform: "scale(1.08)",
    },
  };

  const taskAgentFolderTreeItemSx = {
    px: 0.9,
    py: 0.6,
    minHeight: 34,
    borderRadius: 1.5,
    color: "rgba(231, 236, 255, 0.92)",
    justifyContent: "flex-start",
    textAlign: "left",
    "&:hover": {
      backgroundColor: "rgba(255,255,255,0.08)",
    },
  };

  const renderTaskAgentFolderTree = (nodes, level = 0) =>
    nodes.map((node) => {
      const isActive = Number(node.id) === Number(taskAgentCurrentFolderId);
      const isAncestor = taskAgentFolderPathIds.includes(node.id) && !isActive;
      const hasChildren = node.children?.length > 0;
      const isExpanded = hasChildren ? Boolean(taskAgentFolderExpanded[String(node.id)]) : false;

      return (
        <Box key={`${node.id}-${level}`} sx={{ ml: 5.1 + level * 1.2, mr: 1.2, mb: 0.35 }}>
          <Box
            sx={{
              ...taskAgentFolderTreeItemSx,
              display: "flex",
              alignItems: "center",
              gap: 0.35,
              bgcolor: isActive ? "rgba(120, 136, 255, 0.18)" : isAncestor ? "rgba(255,255,255,0.08)" : "transparent",
              border: isActive
                ? "1px solid rgba(160, 174, 255, 0.28)"
                : isAncestor
                  ? "1px solid rgba(255,255,255,0.1)"
                  : "1px solid transparent",
              color: isActive ? "#fff" : "rgba(231, 236, 255, 0.92)",
            }}
          >
            {hasChildren ? (
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  handleToggleTaskAgentFolder(String(node.id));
                }}
                sx={{
                  p: 0.2,
                  ml: 0.1,
                  color: "inherit",
                }}
              >
                <ExpandMoreRounded
                  sx={{
                    fontSize: 16,
                    transition: `transform 180ms ${SIDEBAR_OPEN_EASING}`,
                    transform: isExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                  }}
                />
              </IconButton>
            ) : (
              <Box sx={{ width: 20, flexShrink: 0 }} />
            )}

            <Box
              role="button"
              tabIndex={0}
              onClick={() => handleTaskAgentFolderNavigation(node.id)}
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === " ") {
                  event.preventDefault();
                  handleTaskAgentFolderNavigation(node.id);
                }
              }}
              sx={{
                flex: 1,
                minWidth: 0,
                cursor: "pointer",
              }}
            >
              <Typography
                noWrap
                title={node.label}
                sx={{ fontSize: "0.8rem", lineHeight: 1.2, fontWeight: isActive ? 700 : 500 }}
              >
                {node.label}
              </Typography>
            </Box>
          </Box>

          {hasChildren && (
            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
              {renderTaskAgentFolderTree(node.children, level + 1)}
            </Collapse>
          )}
        </Box>
      );
    });

  const sidebarContent = (
    <Box
      sx={{
        height: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        color: "#f4f6ff",
        background: sidebarBackground,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.4,
          position: "relative",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          component={NavLink}
          to={homePath}
          sx={{
            width: "100%",
            textAlign: "center",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            textDecoration: "none",
            color: "inherit",
          }}
        >
          {isCvcrmBrand ? (
            <Box
              component="img"
              src="/logoCV-black.svg"
              alt="CVCRM"
              sx={{
                height: 30,
                maxWidth: "100%",
                objectFit: "contain",
                filter: "brightness(0) invert(1)",
              }}
            />
          ) : (
            <Typography variant="subtitle1" fontWeight={700} noWrap sx={{ width: "100%", textAlign: "center" }}>
              {"{Aipim.tech}"}
            </Typography>
          )}
        </Box>
        {isDesktop && (
          <IconButton size="small" onClick={handleHideSidebar} sx={{ color: "inherit", position: "absolute", right: 12 }}>
            <ChevronLeftRounded fontSize="small" />
          </IconButton>
        )}
      </Box>

      <Divider sx={{ borderColor: "rgba(255,255,255,0.12)" }} />

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
        <List sx={{ px: 1.2, py: 1 }}>
          {navSections.map((section) => {
            if (!section.items?.length) {
              const Icon = section.icon;
              const isHome = section.to === homePath;
              const isSectionLink = section.variant === "sectionLink";

              return (
                <Box key={section.label} sx={{ mb: 1.2 }}>
                  <ListItemButton
                    component={NavLink}
                    to={section.to}
                    end={isHome}
                    onClick={() => setMobileDrawerOpen(false)}
                    sx={
                      isSectionLink
                        ? {
                            px: 1.1,
                            py: 0.5,
                            color: "rgba(219, 227, 255, 0.74)",
                            borderRadius: 2,
                            mb: 0.3,
                            "& .MuiListItemIcon-root": {
                              color: "inherit",
                              minWidth: 36,
                            },
                            "&.active": {
                              color: "#ffffff",
                              backgroundColor: isCvcrmDark
                                ? "rgba(126, 214, 188, 0.12)"
                                : isCvcrmBrand
                                  ? "rgba(122, 229, 192, 0.14)"
                                  : "rgba(255,255,255,0.12)",
                            },
                            "&.active .MuiListItemIcon-root": {
                              color: "#ffffff",
                            },
                            "&:hover": {
                              backgroundColor: isCvcrmDark
                                ? "rgba(126, 214, 188, 0.06)"
                                : isCvcrmBrand
                                  ? "rgba(122, 229, 192, 0.08)"
                                  : "rgba(255,255,255,0.08)",
                            },
                          }
                        : navItemSx
                    }
                  >
                    {Icon && (
                      <ListItemIcon>
                        <Icon fontSize="small" />
                      </ListItemIcon>
                    )}
                    <ListItemText
                      primary={section.label}
                      primaryTypographyProps={{
                        noWrap: true,
                        ...(isSectionLink
                          ? {
                              variant: "caption",
                              fontWeight: 700,
                              letterSpacing: 0.5,
                              textTransform: section.label === "{AIPO}" ? "none" : "uppercase",
                            }
                          : {
                              sx: { fontSize: "0.9rem", lineHeight: 1.2, fontWeight: 700 },
                            }),
                      }}
                    />
                  </ListItemButton>
                </Box>
              );
            }

            const sectionExpanded = Boolean(expandedSections[section.label]);
            const visibleItems = section.items;

            return (
              <Box key={section.label} sx={{ mb: 1.2 }}>
                <ListItemButton
                  onClick={() => handleToggleSection(section.label)}
                  aria-expanded={sectionExpanded}
                  sx={{
                    px: 1.1,
                    py: 0.5,
                    color: "rgba(219, 227, 255, 0.74)",
                    borderRadius: 2,
                    mb: 0.3,
                    "&:hover": {
                      backgroundColor: isCvcrmDark
                        ? "rgba(126, 214, 188, 0.06)"
                        : isCvcrmBrand
                          ? "rgba(122, 229, 192, 0.08)"
                          : "rgba(255,255,255,0.08)"
                    },
                  }}
                >
                  <ListItemText
                    primary={section.label}
                    primaryTypographyProps={{
                      variant: "caption",
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      textTransform: "uppercase",
                    }}
                  />
                  <ExpandMoreRounded
                    fontSize="small"
                    sx={{
                      transition: `transform 180ms ${SIDEBAR_OPEN_EASING}`,
                      transform: sectionExpanded ? "rotate(180deg)" : "rotate(0deg)",
                    }}
                  />
                </ListItemButton>
                <Collapse in={sectionExpanded} timeout="auto" unmountOnExit>
                  {visibleItems.map((item) => {
                    const Icon = item.icon;
                    const enabled = item.enabled !== false;
                    const showTaskAgentFolderContext =
                      section.label === "TaskAgent" &&
                      item.to === "/task-agent/pastas" &&
                      location.pathname === "/task-agent/pastas" &&
                      taskAgentFolderTree.length > 0;
                    const processTreeExpanded = Boolean(taskAgentFolderExpanded.__processos__);

                    return (
                      <Box key={item.to} sx={{ ml: 0.4, mb: 0.6 }}>
                        <ListItemButton
                          component={enabled ? NavLink : "button"}
                          to={enabled ? item.to : undefined}
                          disabled={!enabled}
                          onClick={() => handleNavItemClick(item.to, enabled)}
                          sx={{
                            ...navItemSx,
                            opacity: enabled ? 1 : 0.48,
                            cursor: enabled ? "pointer" : "default",
                            mb: showTaskAgentFolderContext ? 0.35 : 0,
                          }}
                        >
                          <ListItemIcon>
                            <Icon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={item.label}
                            primaryTypographyProps={{
                              noWrap: true,
                              sx: { fontSize: "0.9rem", lineHeight: 1.2 },
                            }}
                          />
                          {showTaskAgentFolderContext && (
                            <IconButton
                              size="small"
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                handleToggleTaskAgentFolder("__processos__");
                              }}
                              sx={{ p: 0.2, mr: 0.3, color: "inherit" }}
                            >
                              <ExpandMoreRounded
                                sx={{
                                  fontSize: 18,
                                  transition: `transform 180ms ${SIDEBAR_OPEN_EASING}`,
                                  transform: processTreeExpanded ? "rotate(0deg)" : "rotate(-90deg)",
                                }}
                              />
                            </IconButton>
                          )}
                          {!enabled && <LockOutlined sx={{ fontSize: 17, color: "rgba(245,248,255,0.85)" }} />}
                        </ListItemButton>

                        {showTaskAgentFolderContext && (
                          <Collapse in={processTreeExpanded} timeout="auto" unmountOnExit>
                            <Box sx={{ mt: 0.2 }}>{renderTaskAgentFolderTree(taskAgentFolderTree)}</Box>
                          </Collapse>
                        )}
                      </Box>
                    );
                  })}
                </Collapse>
              </Box>
            );
          })}
        </List>
      </Box>

      <Box sx={{ mt: "auto", p: 1.2, borderTop: "1px solid rgba(255,255,255,0.12)" }}>
        <Box
          role="button"
          tabIndex={0}
          onClick={openUserMenu}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") openUserMenu(event);
          }}
          sx={{
            p: 1.2,
            borderRadius: 2,
            display: "flex",
            alignItems: "center",
            gap: 1,
            cursor: "pointer",
            bgcolor: "rgba(255,255,255,0.06)",
            border: "1px solid rgba(255,255,255,0.12)",
            transition: `transform 180ms ${SIDEBAR_OPEN_EASING}, background-color 220ms ${SIDEBAR_OPEN_EASING}, box-shadow 220ms ${SIDEBAR_OPEN_EASING}`,
            "&:hover": {
              bgcolor: isCvcrmDark
                ? "rgba(126, 214, 188, 0.08)"
                : isCvcrmBrand
                  ? "rgba(122, 229, 192, 0.12)"
                  : "rgba(255,255,255,0.1)",
              transform: "translateY(-1px)",
              boxShadow: isCvcrmDark
                ? "0 10px 20px rgba(4, 16, 14, 0.28), inset 0 0 0 1px rgba(126, 214, 188, 0.12)"
                : isCvcrmBrand
                ? "0 10px 20px rgba(10, 46, 38, 0.35), inset 0 0 0 1px rgba(122, 229, 192, 0.18)"
                : "0 10px 20px rgba(11, 16, 43, 0.35), inset 0 0 0 1px rgba(171, 137, 255, 0.2)",
            },
          }}
        >
          <Avatar sx={{ width: 30, height: 30, bgcolor: isCvcrmBrand ? "rgba(65,178,142,0.88)" : "rgba(91,9,172,0.88)" }}>
            {userInitial}
          </Avatar>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="body2" noWrap fontWeight={700}>
              {userLabel}
            </Typography>
            <Typography variant="caption" noWrap sx={{ color: "rgba(239,243,255,0.82)" }}>
              {roleLabel}
            </Typography>
          </Box>
          <ExpandMoreRounded fontSize="small" />
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        height: { xs: "100dvh", md: "100vh" },
        display: "flex",
        overflow: "hidden",
        bgcolor: "background.default",
      }}
    >
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          width: sidebarHidden ? 0 : DESKTOP_SIDEBAR_WIDTH,
          height: "100%",
          minHeight: 0,
          transition: `width 320ms ${SIDEBAR_OPEN_EASING}`,
          overflow: "hidden",
          flexShrink: 0,
          background: sidebarBackground,
        }}
      >
        <Box
          sx={{
            width: DESKTOP_SIDEBAR_WIDTH,
            height: "100%",
            opacity: sidebarHidden ? 0 : 1,
            transform: sidebarHidden ? "translateX(-14px)" : "translateX(0)",
            transition: `opacity 170ms ${SIDEBAR_OPEN_EASING}, transform 260ms ${SIDEBAR_OPEN_EASING}`,
            pointerEvents: sidebarHidden ? "none" : "auto",
          }}
        >
          {sidebarContent}
        </Box>
      </Box>

      <Drawer
        variant="temporary"
        open={mobileDrawerOpen}
        onClose={() => setMobileDrawerOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": {
            width: DESKTOP_SIDEBAR_WIDTH,
            border: "none",
          },
        }}
      >
        {sidebarContent}
      </Drawer>

      <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}>
        <AppBar
          position="static"
          elevation={0}
          sx={(muiTheme) => ({
            bgcolor: isCvcrmDark
              ? "rgba(12, 21, 28, 0.92)"
              : muiTheme.palette.mode === "dark"
                ? "rgba(11,16,32,0.92)"
                : "rgba(236,239,252,0.92)",
            backdropFilter: "blur(8px)",
            borderBottom:
              isCvcrmDark
                ? "1px solid rgba(104, 171, 150, 0.16)"
                : muiTheme.palette.mode === "dark"
                ? "1px solid rgba(174,184,212,0.18)"
                : "1px solid rgba(35,47,203,0.16)",
            color: "text.primary",
            boxShadow: isCvcrmDark ? "inset 0 -1px 0 rgba(255,255,255,0.02)" : "none",
            backgroundImage: "none",
          })}
        >
          <Toolbar sx={{ gap: 1.2 }}>
            <IconButton edge="start" onClick={handleToggleSidebar} color="inherit" aria-label="menu">
              <MenuRounded />
            </IconButton>
            <Typography
              component={NavLink}
              to={homePath}
              variant="h6"
              noWrap
              title={showHeaderArea ? headerTitle : undefined}
              sx={{ color: "inherit", textDecoration: "none", fontWeight: 700 }}
            >
              {headerTitle}
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Tooltip title={isDarkMode ? "Ativar modo claro" : "Ativar modo escuro"}>
              <IconButton
                color="inherit"
                aria-label={isDarkMode ? "Ativar modo claro" : "Ativar modo escuro"}
                onClick={handleToggleTheme}
              >
                {isDarkMode ? <LightModeRounded /> : <DarkModeRounded />}
              </IconButton>
            </Tooltip>
            <IconButton color="inherit" aria-label="Notificações" onClick={openNotifications}>
              <Badge color="error" badgeContent={notificationsUnread} max={99}>
                <NotificationsRounded />
              </Badge>
            </IconButton>
          </Toolbar>
        </AppBar>

        <Box
          component="main"
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: isAgenteContabilidadeRoute ? "hidden" : "auto",
          }}
        >
          <Container
            maxWidth={isAgenteContabilidadeRoute ? false : "xl"}
            sx={{
              py: 3,
              height: isAgenteContabilidadeRoute ? "100%" : "auto",
            }}
          >
            <Outlet />
          </Container>
        </Box>
      </Box>

      <Menu
        anchorEl={userMenuAnchor}
        open={Boolean(userMenuAnchor)}
        onClose={closeUserMenu}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        transformOrigin={{ vertical: "bottom", horizontal: "right" }}
        PaperProps={{
          sx: (muiTheme) => ({
            minWidth: 260,
            borderRadius: 2,
            border:
              muiTheme.palette.mode === "dark"
                ? "1px solid rgba(174,184,212,0.22)"
                : "1px solid rgba(35,47,203,0.18)",
            bgcolor: "background.paper",
            backgroundImage:
              muiTheme.palette.mode === "dark"
                ? "linear-gradient(170deg, #11182d, #0d1428)"
                : "linear-gradient(170deg, #f8f9ff, #eef0ff)",
          }),
        }}
      >
        <Box sx={{ px: 2, pt: 1.4, pb: 0.8 }}>
          <Typography variant="body2" fontWeight={700}>
            {userLabel}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {roleLabel}
          </Typography>
        </Box>
        <Divider />
        <MenuItem onClick={() => handleUserNavigation("/EditarSenha")}>
          <ListItemIcon>
            <LockResetRounded fontSize="small" />
          </ListItemIcon>
          Alterar senha
        </MenuItem>
        <MenuItem onClick={() => handleUserNavigation("/configuracoes")}>
          <ListItemIcon>
            <SettingsRounded fontSize="small" />
          </ListItemIcon>
          Configurações
        </MenuItem>
        {user.role !== "corretor" && (
          <MenuItem onClick={() => handleUserNavigation("/EditarUsuario")}>
            <ListItemIcon>
              <PeopleAltRounded fontSize="small" />
            </ListItemIcon>
            Usuarios
          </MenuItem>
        )}
        {showCompanies && (
          <MenuItem onClick={handleOpenCompaniesModal} disabled={isLoadingCompanies}>
            <ListItemIcon>
              {isLoadingCompanies ? <CircularProgress size={18} /> : <BusinessRounded fontSize="small" />}
            </ListItemIcon>
            Outras Empresas
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutRounded fontSize="small" />
          </ListItemIcon>
          Sair
        </MenuItem>
      </Menu>

      <Popover
        anchorEl={notificationsAnchor}
        open={Boolean(notificationsAnchor)}
        onClose={closeNotifications}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: (muiTheme) => ({
            mt: 1,
            width: { xs: "calc(100vw - 32px)", sm: 420 },
            maxWidth: "calc(100vw - 32px)",
            borderRadius: 2,
            overflow: "hidden",
            border:
              muiTheme.palette.mode === "dark"
                ? "1px solid rgba(174,184,212,0.22)"
                : "1px solid rgba(35,47,203,0.16)",
            bgcolor: "background.paper",
            backgroundImage:
              muiTheme.palette.mode === "dark"
                ? "linear-gradient(170deg, #11182d, #0d1428)"
                : "linear-gradient(170deg, #f8f9ff, #eef0ff)",
          }),
        }}
      >
        <Box sx={{ p: 1.6, display: "flex", alignItems: "center", gap: 1 }}>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography fontWeight={900}>Notificações</Typography>
            <Typography variant="caption" color="text.secondary">
              {notificationsUnread ? `${notificationsUnread} não lida(s)` : "Nenhuma notificação pendente"}
            </Typography>
          </Box>
          <IconButton
            size="small"
            color={notificationSoundEnabled ? "primary" : "default"}
            aria-label={notificationSoundEnabled ? "Desativar som de notificacoes" : "Ativar som de notificacoes"}
            title={notificationSoundEnabled ? "Som ligado" : "Som desligado"}
            onClick={handleToggleNotificationSound}
          >
            {notificationSoundEnabled ? <VolumeUpRounded fontSize="small" /> : <VolumeOffRounded fontSize="small" />}
          </IconButton>
          <Button
            size="small"
            onClick={handleMarkAllNotificationsRead}
            disabled={!notificationsUnread || loadingNotifications}
          >
            Marcar todas
          </Button>
        </Box>
        <Divider />
        <Box sx={{ maxHeight: 440, overflowY: "auto" }}>
          {loadingNotifications ? (
            <Box sx={{ p: 3, display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} />
            </Box>
          ) : notificationsError ? (
            <Box sx={{ p: 2 }}>
              <Typography variant="body2" color="error">
                {notificationsError}
              </Typography>
            </Box>
          ) : notifications.length ? (
            notifications.map((notification) => {
              const notificationId = getNotificationId(notification);
              const isUnread = !isNotificationRead(notification);
              const isLeadNotification = getNotificationType(notification) === "lead";

              return (
                <Box
                  key={notificationId || `${notification.titulo || "notificacao"}-${notification.dataCriacao || ""}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleNotificationClick(notification)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      handleNotificationClick(notification);
                    }
                  }}
                  sx={{
                    px: 1.6,
                    py: 1.35,
                    cursor: isUnread || isLeadNotification ? "pointer" : "default",
                    borderBottom: "1px solid",
                    borderColor: "divider",
                    bgcolor: isUnread ? "action.hover" : "transparent",
                    "&:hover": {
                      bgcolor: "action.selected",
                    },
                  }}
                >
                  <Box sx={{ display: "flex", gap: 1.1, alignItems: "flex-start" }}>
                    <Box
                      sx={{
                        width: 9,
                        height: 9,
                        mt: 0.65,
                        borderRadius: "50%",
                        flexShrink: 0,
                        bgcolor: isUnread ? "error.main" : "transparent",
                        border: isUnread ? "none" : "1px solid",
                        borderColor: "divider",
                      }}
                    />
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" fontWeight={isUnread ? 900 : 700} noWrap>
                        {notification.titulo || "Notificacao"}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                        {notification.mensagem || ""}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.55 }}>
                        {formatNotificationDate(notification.dataCriacao)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })
          ) : (
            <Box sx={{ p: 3, textAlign: "center" }}>
              <Typography fontWeight={800}>Sem notificações</Typography>
              <Typography variant="body2" color="text.secondary">
                Novos avisos aparecerao aqui.
              </Typography>
            </Box>
          )}
        </Box>
      </Popover>

      <AdminCompaniesModal
        open={openCompaniesModal}
        onClose={() => setOpenCompaniesModal(false)}
        companies={companies}
        onSelectCompany={handleSelectCompany}
        isLoading={isLoadingCompanies}
      />
    </Box>
  );
};

export default AppShell;
